import { clamp } from 'lodash-es';

import { css, styled } from '@mui/material';

import Skeleton from '../Skeleton/Skeleton';
import { skeletonClasses } from '../Skeleton/theme';
import { actionBarSkeletonClasses } from './theme';

import type { ActionBarSkeletonOwnerState, ActionBarSkeletonProps } from './types';

export default function ActionBarSkeleton(props: ActionBarSkeletonProps) {
  const {
    actionsCount = 2,
    paginationType = 'numbers',
    showActions = true,
    showFilter = true,
    showPagination = true,
    showRefetch = true,
    showSetting = true,
    showSideMargin = false,
    ...others
  } = props;

  const ownerState: ActionBarSkeletonOwnerState = {
    ...props,
    actionsCount,
    paginationType,
    showActions,
    showFilter,
    showPagination,
    showRefetch,
    showSetting,
    showSideMargin,
  };

  const actions = Array(clamp(actionsCount, 1, 5)).fill(1);

  return (
    <ActionBarSkeletonRoot {...others} ownerState={ownerState}>
      <ActionBarSkeletonToolbar ownerState={ownerState}>
        <ActionBarSkeletonToolbarLeft ownerState={ownerState}>
          {showFilter && (
            <ActionBarSkeletonFilterButton ownerState={ownerState}>
              <Skeleton shape="rectangle" />
            </ActionBarSkeletonFilterButton>
          )}
          {showActions && actions.length > 0 && (
            <ActionBarSkeletonActions ownerState={ownerState}>
              {actions.map((_, i) => (
                <Skeleton key={i} shape="rectangle" />
              ))}
            </ActionBarSkeletonActions>
          )}
          {showSetting && (
            <ActionBarSkeletonSettingButton ownerState={ownerState}>
              <Skeleton shape="rectangle" />
            </ActionBarSkeletonSettingButton>
          )}
        </ActionBarSkeletonToolbarLeft>
        <ActionBarSkeletonToolbarRight ownerState={ownerState}>
          {showRefetch && (
            <ActionBarSkeletonRefetchButton ownerState={ownerState}>
              <Skeleton shape="rectangle" />
            </ActionBarSkeletonRefetchButton>
          )}
          {showPagination && (
            <ActionBarSkeletonPagination ownerState={ownerState}>
              <Skeleton shape="rectangle" />
            </ActionBarSkeletonPagination>
          )}
        </ActionBarSkeletonToolbarRight>
      </ActionBarSkeletonToolbar>
    </ActionBarSkeletonRoot>
  );
}

const ActionBarSkeletonRoot = styled('div', {
  target: actionBarSkeletonClasses.root,
})<{ ownerState: ActionBarSkeletonOwnerState }>(
  ({ theme, ownerState }) => css`
    position: relative;
    padding: ${ownerState.showSideMargin ? '0 32px 0 24px' : '0 16px 0 6px'};

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      border-top: ${theme.mixins.border('border/weak/default')};
    }
  `,
);

const ActionBarSkeletonToolbar = styled('div', {
  target: actionBarSkeletonClasses.toolbar,
})<{ ownerState: ActionBarSkeletonOwnerState }>(
  () => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-height: 48px;
  `,
);

const ActionBarSkeletonToolbarLeft = styled('div', {
  target: actionBarSkeletonClasses.toolbarLeft,
})<{ ownerState: ActionBarSkeletonOwnerState }>(
  () => css`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-grow: 1;
    padding: 0 10px;
  `,
);

const ActionBarSkeletonToolbarRight = styled('div', {
  target: actionBarSkeletonClasses.toolbarRight,
})<{ ownerState: ActionBarSkeletonOwnerState }>(
  () => css`
    display: flex;
    align-items: center;
    gap: 8px;
  `,
);

const ActionBarSkeletonFilterButton = styled('div', {
  target: actionBarSkeletonClasses.filterButton,
})<{ ownerState: ActionBarSkeletonOwnerState }>(
  () => css`
    .${skeletonClasses.root} {
      width: 92px;
      height: 32px;
    }
  `,
);

const ActionBarSkeletonActions = styled('div', {
  target: actionBarSkeletonClasses.actions,
})<{ ownerState: ActionBarSkeletonOwnerState }>(
  () => css`
    display: flex;
    align-items: center;
    gap: 8px;

    .${skeletonClasses.root} {
      width: 60px;
      height: 32px;
    }
  `,
);

const ActionBarSkeletonSettingButton = styled('div', {
  target: actionBarSkeletonClasses.settingButton,
})<{ ownerState: ActionBarSkeletonOwnerState }>(
  () => css`
    .${skeletonClasses.root} {
      width: 60px;
      height: 32px;
    }
  `,
);

const ActionBarSkeletonRefetchButton = styled('div', {
  target: actionBarSkeletonClasses.refetchButton,
})<{ ownerState: ActionBarSkeletonOwnerState }>(
  () => css`
    .${skeletonClasses.root} {
      width: 32px;
      height: 32px;
    }
  `,
);

const ActionBarSkeletonPagination = styled('div', {
  target: actionBarSkeletonClasses.pagination,
})<{ ownerState: ActionBarSkeletonOwnerState }>(
  ({ ownerState }) => css`
    .${skeletonClasses.root} {
      ${ownerState.paginationType === 'numbers' &&
      css`
        width: 230px;
        height: 32px;
      `}
      ${ownerState.paginationType === 'compact' &&
      css`
        width: 80px;
        height: 32px;
      `}
    }
  `,
);
